<script lang="ts" setup>
import {useI18n} from 'vue-i18n';
import {computed, ref} from 'vue';
import useCpBem from '~/composables/useCpBem';
import CpText from '~/components/cp/CpText/CpText.vue';
import CpButtonIcon from '~/components/cp/CpButtonIcon/CpButtonIcon.vue';
import CpDivider from '~/components/cp/CpDivider/CpDivider.vue';
import type {IRadioButtonItem} from '~/types';
import CpIconStandard from '~/components/cp/CpIcon/CpIconStandard/CpIconStandard.vue';

const props = withDefaults(
  defineProps<{
    items: IRadioButtonItem<string>[]
    title: string

    // Props Aux
    disableExpand?: boolean
    hideAmount?: boolean
  }>(), {
    disableExpand: false,
    hideAmount: false,
  },
);

const model = defineModel();

const {b, e} = useCpBem('cp-dropdown-section-radio');
const {t} = useI18n();
const expand = ref<boolean>(true);
const allValues = ref<boolean>(false);
const minItems = ref<number>(5);

defineEmits<{
  (e: 'change-value', value: string): void
}>();

const itemSelected = computed<IRadioButtonItem<string> | null>(() => {
  const selected = props.items.find((a) => a.value === model.value);
  if (selected) {
    return selected;
  }
  return null;
});

</script>
<template>
    <div :class="b">
        <div :class="e('container-dropdown-header')" @click="expand = disableExpand || !expand">
            <div :class="e('text-dropdown')">
                <CpText variant="subheading">
                    {{ title }}
                </CpText>
                <CpText variant="caption-bold" type="primary">
                    {{ itemSelected?.label ?? '' }}
                </CpText>
            </div>
            <div v-if="!disableExpand" :class="e('toggle-icon')">
                <CpButtonIcon :kind="expand ? 'expand_less' : 'expand_more'" />
            </div>
        </div>

        <Transition name="list">
            <div v-if="expand">
                <CpDivider />
                <v-radio-group
                    v-model="model"
                    hide-details
                    @update:model-value="$emit('change-value', $event as string)"
                >
                    <TransitionGroup name="element" tag="div">
                        <div
                            v-for="item in (allValues ? items : items.slice(0, minItems))"
                            :key="item.value"
                            :class="e`radio-container`"
                            data-testid="filters-order-by"
                        >
                            <v-radio :value="item.value" color="#0066BB" :ripple="false">
                                <template #label>
                                    <div :class="[e`radio-text`,'pl-1']">
                                        <CpText
                                            :variant="item.value === model ? 'body-bold' : 'body-regular'"
                                            type="primary"
                                        >
                                            {{ item.label }}
                                        </CpText>
                                        <CpText :variant="item.value === model ? 'body-bold' : 'body-regular'">
                                            {{ item.quantity }}
                                        </CpText>
                                    </div>
                                </template>
                            </v-radio>
                        </div>
                    </TransitionGroup>
                    <div v-if="items.length > minItems" :class="e`toggle-container`">
                        <div :class="e('show-more-btn')" @click="allValues = !allValues">
                            <CpIconStandard
                                :icon="allValues ? 'expand_less' : 'expand_more'"
                                type="primary"
                                kind="standard"
                            />
                            <CpText
                                variant="body-bold"
                                type="primary"
                            >
                                {{ allValues ? t('showLess') : t('showMore', {count: items.length - minItems}) }}
                            </CpText>
                        </div>
                    </div>
                </v-radio-group>
            </div>
        </Transition>
    </div>
</template>
<style scoped lang="scss">
@import "assets/style/global";
.cp-dropdown-section-radio {

  &__show-more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 12px $cp-gutter;
    gap: $cp-gutter-small;
    height: $cp-gutter-xlarge;
    border-radius: $cp-gutter-small;
  }

  &__toggle-container {
    padding: $cp-gutter-small $cp-gutter;
  }

  &__toggle-icon {
    display: flex;
    align-items: center;
  }

  .list-enter-active,
  .list-leave-active {
    transition: all .5s ease;
    max-height: 300px;
    overflow: hidden;
  }
  .list-enter-from,
  .list-leave-to {
    opacity: 0;
    max-height: 0;
  }

  .element-enter-active,
  .element-leave-active {
    transition: all .5s ease;
    max-height: 60px;
  }
  .element-enter-from,
  .element-leave-to {
    opacity: 0;
    max-height: 0;
    padding: 0 $cp-gutter;
  }

  &__container-dropdown-header {
    padding: $cp-gutter-small $cp-gutter;
    display: flex;
    flex-direction: row;
    gap: $cp-gutter-small;
    min-height: 44px;
    cursor: pointer;
  }
  &__text-dropdown {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  &__radio-container {
    padding: $cp-gutter-small $cp-gutter;
  }
  &__radio-text {
    width: 100%;
    display: flex;
    justify-content: space-between;
  }
  &:deep(.v-label) {
    opacity: unset;
  }
  &:deep(.v-selection-control__input > .v-icon){
    color: var(--color-primary);
    opacity: unset;
  }
  &:deep(.v-selection-control__wrapper) {
    width: unset;
    height: unset;
  }
  &:deep(.v-selection-control__input) {
    height: unset;
    width: unset;
  }
  &:deep(.v-btn-group--density-default.v-btn-group) {
    height: unset;
  }
  &:deep(.v-label) {
    width: 100%;
  }
}
</style>
<i18n locale="es-US" lang="yaml">
  showMore: 'Mostrar {count} más'
  showLess: 'Mostrar menos'
</i18n>
